/**
 * `<brita-subscription-widget>` custom element.
 *
 * Manages the one-time vs. subscription purchase toggle and keeps the product
 * form, URL params, and FoxTheme pubsub in sync when variants or selling plans change.
 *
 * Two operating modes, detected from DOM presence at connect time:
 *
 * without-variant-select (show_variant_selector: false)
 *   The active variant is controlled by an external FoxTheme variant picker.
 *   The widget listens to `optionValueSelectionChange` pubsub events, rebuilds
 *   the selling-plan `<select>` for the newly selected variant, and updates
 *   form + URL accordingly.
 *
 * with-variant-select (show_variant_selector: true)
 *   A single combined `<select>` encodes every variant × selling-plan pair.
 *   The one-time section shows its own variant option inputs.
 *   Both controls update variant ID and selling plan simultaneously.
 *
 * Required data attributes on the host element:
 *   data-form-id          — ID of the associated product form
 *   data-variant-id       — ID of the initially selected variant
 *   data-money-format     — Shopify money format string
 *
 * Required child element:
 *   [data-product-json]   — `<script type="application/json">` with full product JSON
 */
if (!customElements.get("brita-subscription-widget")) {
  customElements.define(
    "brita-subscription-widget",
    class BritaSubscriptionWidget extends HTMLElement {
      // ── Lifecycle ─────────────────────────────────────────────────────────────

      connectedCallback() {
        this._form = document.getElementById(this.dataset.formId);
        this._productData = JSON.parse(
          this.querySelector("[data-product-json]").textContent,
        );
        // Prefer the theme's active money format (respects the storefront/market
        // currency) over the host data attribute, which only carries the shop's
        // primary money_format.
        this._moneyFormat =
          window.FoxTheme?.settings?.moneyFormat ||
          this.dataset.moneyFormat ||
          "{{amount}}";
        this._variantId = parseInt(this.dataset.variantId);

        // Upsell mode: widget is self-contained; all pubsub is disabled.
        this._isUpsell = this.dataset.upsell === "true";
        this._isQuickView = this.closest("quick-view-modal") ? true : false;
        this._productInfoElement = this.closest("product-info");
        // Resolved after _form is set below; used by _updateAtcButtonState.
        this._atcSubmitBtn = null;

        this._planSelect = this.querySelector("[data-selling-plan-select]");
        this._variantPlanSelect = this.querySelector(
          "[data-variant-plan-select]",
        );
        this._deliverySection = this.querySelector("[data-delivery-section]");
        this._variantSelector = this.querySelector("[data-variant-selector]");
        this._radios = this.querySelectorAll("[data-purchase-type]");

        // Detect mode from DOM presence — avoids relying on Liquid boolean → string casting
        this._scenario = this._variantPlanSelect
          ? "with-variant-select"
          : "without-variant-select";

        /** @type {Object.<string, Object>} Flat map of selling plan id → plan object for quick lookup */
        this._plansById = {};
        (this._productData.selling_plan_groups || []).forEach((group) => {
          (group.selling_plans || []).forEach((plan) => {
            this._plansById[String(plan.id)] = plan;
          });
        });

        // without-variant-select: option-value-id combination → variant-id lookup map
        if (this._scenario === "without-variant-select") {
          const mapEl = this.querySelector("[data-option-value-map]");
          this._optionValueMap = mapEl ? JSON.parse(mapEl.textContent) : {};
        }

        // Cache ATC button now that this._form is resolved.
        if (this._isUpsell) {
          this._atcSubmitBtn = this._form?.querySelector('[type="submit"][name="add"]') ?? null;
        }

        this._bindEvents();
        this._initState();
      }

      /**
       * Unsubscribes all FoxTheme pubsub listeners to prevent memory leaks.
       */
      disconnectedCallback() {
        this._unsubOptions?.();
        this._unsubVariant?.();
      }

      // ── Initialisation ────────────────────────────────────────────────────────

      /**
       * Wires DOM event listeners and FoxTheme pubsub subscriptions.
       */
      _bindEvents() {
        this._radios.forEach((r) =>
          r.addEventListener("change", () => this._applyPurchaseType(r.value, true, r)),
        );
        this._planSelect?.addEventListener("change", () =>
          this._onPlanChange(),
        );
        this._variantPlanSelect?.addEventListener("change", () =>
          this._onVariantPlanChange(),
        );

        if (this._scenario === "with-variant-select") {
          this.querySelectorAll("[data-option-position]").forEach((input) => {
            input.addEventListener("change", (e) =>
              this._onOnetimeVariantChange(e),
            );
          });
        }

        // Upsell mode: the embedded product-form element handles ATC, loading,
        // errors and cart updates natively. Skip all FoxTheme pubsub so this
        // widget never interferes with the main product on the page.
        if (this._isUpsell) return;

        if (window.FoxTheme?.pubsub) {
          this._unsubOptions = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
            (evt) => this._onExternalVariantChange(evt),
          );
          // product-info.updateURL strips selling_plan after every variant re-render — put it back
          this._unsubVariant = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            ({ data: { html } = {} } = {}) => {
              this._restoreSellingPlanUrl();
              this._updateVariantNote(html);
            },
          );
        }
      }

      /**
       * Applies the initial purchase-type state from whichever radio is pre-checked.
       * No mode class is set on init so the CSS default (delivery open, selector
       * hidden) applies without triggering any animations.
       */
      _initState() {
        const checked = this.querySelector("[data-purchase-type]:checked");
        if (checked) this._applyPurchaseType(checked.value, false);
      }

      /**
       * Re-adds `selling_plan` to the URL after `product-info` strips it during a
       * variant re-render. Only runs when subscription mode is active.
       */
      _restoreSellingPlanUrl() {
        if (this._isUpsell) return;
        if (!this.querySelector('[data-purchase-type="subscription"]:checked'))
          return;
        const planId = this._form?.querySelector(
          '[name="selling_plan"]',
        )?.value;
        if (planId) this._updateUrlParam("selling_plan", planId);
      }

      // ── Purchase type toggle ──────────────────────────────────────────────────

      /**
       * Switches purchase mode and updates form/URL state.
       * When animate is true (every user interaction), the mode class is toggled
       * on the widget root and CSS keyframes handle both the delivery and the
       * variant selector simultaneously. On page-load init (animate = false) no
       * class is set — the CSS default state (delivery open, selector hidden) is
       * already correct so no visual change is needed.
       *
       * @param {'onetime'|'subscription'} type
       * @param {boolean} animate
       * @param {Element|null} source — the element the user actually interacted
       *   with (e.g. the clicked radio). Published as the focus target so
       *   product-info refocuses there instead of the select. On init
       *   (animate = false) focus is skipped entirely.
       */
      _applyPurchaseType(type, animate = true, source = null) {
        const skipFocus = !animate;

        // GA4 switch_purchase_type — fired on user-initiated toggles (animate
        // === true). The init call (animate === false) only seeds the
        // previous-type baseline stored below.
        try {
          if (animate && window.BritaGx) {
            const typeMap = { onetime: "one_time_purchase", subscription: "subscription" };
            const prev =
              this._gxPrevPurchaseType ||
              (type === "subscription" ? "onetime" : "subscription");
            const pd = this._productData || {};
            const vid = this._variantId;
            const variant = (pd.variants || []).find((v) => v.id === vid);
            window.BritaGx.track("switch_purchase_type", {
              selected_purchase_type: typeMap[type] || type,
              previous_purchase_type: typeMap[prev] || prev,
              item_id: (variant && variant.sku) || String(pd.id || ""),
              item_name: pd.title || "",
            });
          }
        } catch (e) {
          /* ignore */
        }
        this._gxPrevPurchaseType = type;

        if (animate) {
          this.classList.toggle("bsw--onetime", type === "onetime");
          this.classList.toggle("bsw--subscription", type === "subscription");
        }

        if (type === "subscription") {
          if (this._scenario === "with-variant-select") {
            const sel =
              this._variantPlanSelect?.options[
                this._variantPlanSelect.selectedIndex
              ];
            if (sel) {
              this._setFormVariant(parseInt(sel.dataset.variantId));
              this._setSellingPlan(sel.dataset.planId);
              this._updateUrlParam("variant", sel.dataset.variantId);
              this._updateUrlParam("selling_plan", sel.dataset.planId);
              this._updatePriceDisplay(
                parseInt(sel.dataset.price),
                parseInt(sel.dataset.compareAt),
              );
              // Keep the one-time section (price + selector) tracking the same
              // variant the subscription plan belongs to, so its price is correct
              // on load even while the selector itself is collapsed.
              this._syncOnetimeToVariant(parseInt(sel.dataset.variantId));
            }
            this._publishVariantPlanOptionValues(source, { skipFocus });
          } else {
            const opt =
              this._planSelect?.options[this._planSelect.selectedIndex];
            if (opt?.value) {
              this._setSellingPlan(opt.value);
              this._updateUrlParam("selling_plan", opt.value);
              this._updatePriceDisplay(
                parseInt(opt.dataset.price),
                parseInt(opt.dataset.compareAt),
              );
            }
            this._publishOptionValues(source, { skipFocus });
          }
        } else {
          this._clearSellingPlan();
          this._removeUrlParam("selling_plan");
          if (this._scenario === "with-variant-select") {
            const activeSel =
              this._variantPlanSelect?.options[
                this._variantPlanSelect.selectedIndex
              ];
            if (activeSel)
              this._syncOnetimeToVariant(parseInt(activeSel.dataset.variantId));
            this._publishVariantPlanOptionValues(source, { skipFocus });
          } else {
            this._publishOptionValues(source, { skipFocus });
          }
        }
      }

      // ── Without variant select: plan-only select ──────────────────────────────

      /**
       * Handles a user change on the selling-plan `<select>` (without-variant-select).
       * Updates the form, URL, and subscription price display.
       */
      _onPlanChange() {
        const opt = this._planSelect.options[this._planSelect.selectedIndex];
        this._setSellingPlan(opt.value);
        this._updateUrlParam("selling_plan", opt.value);
        this._updatePriceDisplay(
          parseInt(opt.dataset.price),
          parseInt(opt.dataset.compareAt),
        );
        this._publishOptionValues();
      }

      /**
       * Reacts to an external variant-picker change.
       * Ignores events that originate from within this widget, and events from
       * a different `product-info` context (e.g. a quick-view modal for another
       * product on the same page), to avoid loops and cross-talk.
       *
       * @param {{ data: { selectedOptionValues: string[], target: Element } }} evt
       */
      _onExternalVariantChange({
        data: { selectedOptionValues, target } = {},
      }) {
        if (!selectedOptionValues?.length || this.contains(target)) return;
        if (
          this._productInfoElement &&
          target?.closest?.("product-info") !== this._productInfoElement
        )
          return;

        if (this._scenario === "with-variant-select") {
          // Reflect each externally selected option value onto the widget's own
          // radio buttons and select elements, then sync the subscription combined select.
          this._applyExternalOptionValues(selectedOptionValues);
          return;
        }

        // Without variant select ───────────────────────────────────────────────────
        // Primary lookup: option-value-ID map built from Liquid.
        // Fallback: read the variant ID directly from the product form — FoxTheme
        // updates [name="id"] synchronously before publishing this event, so the
        // value is already correct when we arrive here.
        let variantId = this._optionValueMap[selectedOptionValues.join(",")];
        if (!variantId) {
          const formId = parseInt(
            this._form?.querySelector('[name="id"]')?.value,
          );
          if (formId) variantId = formId;
        }

        if (!variantId) return;
        this._variantId = variantId;
        const variant = this._productData.variants.find(
          (v) => v.id === variantId,
        );
        if (variant) {
          this._updateOnetimePrice(variant.price, variant.compare_at_price);
          this._rebuildPlanSelect(variant);
        }
      }

      /**
       * With-variant-select: mirrors an externally published option-value selection
       * onto the widget's own radio buttons / select elements and keeps the
       * subscription combined select in sync.
       *
       * @param {string[]} selectedOptionValues — option-value IDs from the pubsub event
       */
      _applyExternalOptionValues(selectedOptionValues) {
        if (this._variantSelector) {
          selectedOptionValues.forEach((valueId) => {
            const strId = String(valueId);

            // Radio buttons — check the matching input and update the visible label
            const radio = this._variantSelector.querySelector(
              `input[type="radio"][data-option-value-id="${strId}"]`,
            );
            if (radio) {
              radio.checked = true;
              const labelEl = radio
                .closest(".product-form__input")
                ?.querySelector("[data-selected-value]");
              if (labelEl) labelEl.textContent = radio.value;
            }

            // Select elements — set the matching option as selected
            const opt = this._variantSelector.querySelector(
              `option[data-option-value-id="${strId}"]`,
            );
            if (opt?.parentElement?.tagName === "SELECT") {
              opt.parentElement.value = opt.value;
            }
          });
        }

        // One-time price: resolve the variant from the (now-synced) one-time
        // selector so it updates even for variants that have no subscription
        // option in the combined select (and therefore no matching option below).
        const onetimeVariant = this._variantFromOnetimeSelector();
        if (onetimeVariant)
          this._updateOnetimePrice(
            onetimeVariant.price,
            onetimeVariant.compare_at_price,
          );

        // Sync the subscription combined select to the matching variant.
        // The option's data-option-value-ids is the same comma-joined format the
        // pubsub event uses, so a direct string comparison is sufficient.
        const optIdsStr = selectedOptionValues.map(String).join(",");
        const matchOpt = Array.from(
          this._variantPlanSelect?.options ?? [],
        ).find((o) => o.dataset.optionValueIds === optIdsStr);
        if (!matchOpt) return;

        this._syncSubscriptionToVariant(parseInt(matchOpt.dataset.variantId));
      }

      /**
       * Rebuilds the selling-plan `<select>` options for a new variant (without-variant-select).
       * Preserves the previously selected plan where possible.
       * When subscription mode is active and the plan ID changes, re-publishes
       * `optionValueSelectionChange` so `product-info` aborts its stale fetch and
       * re-fetches with the correct `selling_plan` URL param.
       *
       * @param {Object} variant — Shopify variant object from product JSON
       */
      _rebuildPlanSelect(variant) {
        const select = this._planSelect;
        if (!select) return;
        const prevPlanId = select.value;
        const allocations = variant.selling_plan_allocations || [];
        const keepPlan = allocations.some(
          (a) => String(a.selling_plan_id) === prevPlanId,
        );

        select.innerHTML = "";
        allocations.forEach((alloc, i) => {
          const plan = this._plansById[String(alloc.selling_plan_id)];
          const compareAt = Math.max(
            variant.compare_at_price || 0,
            alloc.compare_at_price || 0,
          );
          const opt = document.createElement("option");
          opt.value = alloc.selling_plan_id;
          opt.dataset.description = plan?.description || "";
          opt.dataset.price = alloc.price;
          opt.dataset.compareAt = compareAt;
          opt.textContent = plan?.name || String(alloc.selling_plan_id);
          opt.selected = keepPlan
            ? String(alloc.selling_plan_id) === prevPlanId
            : i === 0;
          select.appendChild(opt);
        });

        const sel = select.options[select.selectedIndex];
        if (sel) {
          this._updatePriceDisplay(
            parseInt(sel.dataset.price),
            parseInt(sel.dataset.compareAt),
          );
          const isSubscriptionActive = !!this.querySelector(
            '[data-purchase-type="subscription"]:checked',
          );
          if (isSubscriptionActive) {
            this._setSellingPlan(sel.value);
            this._updateUrlParam("selling_plan", sel.value);
            // If the plan changed, re-trigger product-info so it fetches with the correct selling_plan.
            // The abort in renderProductInfo cancels the stale in-flight request from the external variant change.
            // skipFocus: the user interacted with the external picker, not this
            // widget — keep focus there instead of yanking it onto the select.
            if (String(sel.value) !== String(prevPlanId)) {
              this._publishOptionValues(this._planSelect, { skipFocus: true });
            }
          }
        }
      }

      /**
       * Publishes `optionValueSelectionChange` for the currently tracked variant
       * so `product-info` keeps prices, images, and option availability in sync.
       * Uses a target inside this widget so `_onExternalVariantChange` skips it
       * (target is inside this widget). Defaults to `this._planSelect`; callers
       * may pass the element the user actually interacted with so product-info
       * refocuses there.
       *
       * @param {Element} [target] — focus target (defaults to the plan select)
       * @param {{skipFocus?: boolean}} [options]
       */
      _publishOptionValues(target, { skipFocus = false } = {}) {
        if (this._isUpsell || !window.FoxTheme?.pubsub || !this._planSelect) return;
        const focusTarget = target || this._planSelect;
        const optValueIds = this._currentOptionValueIds();
        if (!optValueIds.length) return;
        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          {
            data: {
              event: { target: focusTarget },
              target: focusTarget,
              selectedOptionValues: optValueIds,
              skipFocus,
            },
          },
        );
      }

      /**
       * With-variant-select counterpart of `_publishOptionValues`.
       * Reads option-value IDs from the currently selected variant-plan option.
       * Must be called after URL params are already updated so `product-info`
       * picks up the correct `selling_plan` when it builds the fetch URL.
       *
       * @param {Element} [target] — focus target (defaults to the variant-plan select)
       * @param {{skipFocus?: boolean}} [options]
       */
      _publishVariantPlanOptionValues(target, { skipFocus = false } = {}) {
        if (this._isUpsell || !window.FoxTheme?.pubsub || !this._variantPlanSelect) return;
        const sel =
          this._variantPlanSelect.options[
            this._variantPlanSelect.selectedIndex
          ];
        const optIds = (sel?.dataset.optionValueIds || "")
          .split(",")
          .filter(Boolean);
        if (!optIds.length) return;
        const focusTarget = target || this._variantPlanSelect;
        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          {
            data: {
              event: { target: focusTarget },
              target: focusTarget,
              selectedOptionValues: optIds,
              skipFocus,
            },
          },
        );
      }

      /**
       * Reverse-looks up the option-value-ID array for `this._variantId` from
       * the embedded option-value map (without-variant-select).
       *
       * @returns {string[]}
       */
      _currentOptionValueIds() {
        for (const [key, id] of Object.entries(this._optionValueMap || {})) {
          if (id === this._variantId) return key.split(",").filter(Boolean);
        }
        return [];
      }

      // ── With variant select: combined variant × plan select ───────────────────

      /**
       * Handles a user change on the combined variant × selling-plan `<select>`
       * (with-variant-select). Updates form inputs, URL params, price display,
       * and notifies `product-info`.
       */
      _onVariantPlanChange() {
        const sel =
          this._variantPlanSelect.options[
            this._variantPlanSelect.selectedIndex
          ];
        const varId = parseInt(sel.dataset.variantId);
        const planId = sel.dataset.planId;

        this._setFormVariant(varId);
        this._setSellingPlan(planId);
        this._updateUrlParam("variant", varId);
        this._updateUrlParam("selling_plan", planId);
        this._updatePriceDisplay(
          parseInt(sel.dataset.price),
          parseInt(sel.dataset.compareAt),
        );
        this._syncOnetimeToVariant(varId);
        this._updateAtcButtonState(
          this._productData.variants.find((v) => v.id === varId),
        );

        const optIds = (sel.dataset.optionValueIds || "")
          .split(",")
          .filter(Boolean);
        if (!this._isUpsell && optIds.length && window.FoxTheme?.pubsub) {
          FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
            {
              data: {
                event: { target: this._variantPlanSelect },
                target: this._variantPlanSelect,
                selectedOptionValues: optIds,
              },
            },
          );
        }
      }

      // ── With variant select: one-time variant selector (radio buttons or select) ─

      /**
       * Handles a variant option change inside the one-time section
       * (with-variant-select). Collects the full option selection, matches it to
       * a variant, updates the form + URL, and keeps the subscription dropdown
       * in sync.
       *
       * @param {Event} e — change event from a radio input or `<select>`
       */
      _onOnetimeVariantChange(e) {
        if (!this._variantSelector) return;
        const values = {};
        const optValueIds = [];

        // Collect from checked radio inputs
        this._variantSelector
          .querySelectorAll('input[type="radio"]:checked')
          .forEach((inp) => {
            const pos = inp.dataset.optionPosition;
            if (!pos) return;
            values[pos] = inp.value;
            if (inp.dataset.optionValueId)
              optValueIds.push(inp.dataset.optionValueId);
          });

        // Collect from select elements
        this._variantSelector
          .querySelectorAll("select[data-option-position]")
          .forEach((sel) => {
            const opt = sel.options[sel.selectedIndex];
            if (!opt) return;
            values[sel.dataset.optionPosition] = opt.value;
            if (opt.dataset.optionValueId)
              optValueIds.push(opt.dataset.optionValueId);
          });
        // Collect from hidden inputs (for options that are hidden)
        this._variantSelector
          .querySelectorAll('input[data-hidden-option]')
          .forEach((inp) => {
            const pos = inp.dataset.optionPosition;
            if (!pos) return;
            values[pos] = inp.value;
            if (inp.dataset.optionValueId)
              optValueIds.push(inp.dataset.optionValueId);
          });
        // Update the inline selected-value label for button-style pickers
        const fieldset = e.target.closest(".product-form__input");
        const selectedValueEl = fieldset?.querySelector(
          "[data-selected-value]",
        );
        if (selectedValueEl) {
          selectedValueEl.textContent =
            e.target.tagName === "SELECT"
              ? e.target.options[e.target.selectedIndex]?.text
              : e.target.value;
        }

        // Match values against product variants (options array is 0-indexed, positions are 1-indexed)
        const variant = this._productData.variants.find((v) =>
          (v.options || []).every((val, i) => values[String(i + 1)] === val),
        );
        if (!variant) return;

        this._setFormVariant(variant.id);
        this._updateUrlParam("variant", variant.id);
        this._updateOnetimePrice(variant.price, variant.compare_at_price);
        this._syncSubscriptionToVariant(variant.id);
        this._updateAtcButtonState(variant);

        if (!this._isUpsell && optValueIds.length && window.FoxTheme?.pubsub) {
          FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
            {
              data: {
                event: e,
                target: e.target,
                selectedOptionValues: optValueIds,
              },
            },
          );
        }
      }

      /**
       * Resolves the product variant currently reflected by the one-time variant
       * selector (radio buttons and/or selects), or null if none matches.
       * Lets the one-time price stay in sync even when the variant has no
       * subscription option in the combined select.
       *
       * @returns {Object|null} product JSON variant object
       */
      _variantFromOnetimeSelector() {
        if (!this._variantSelector) return null;
        const values = {};
        this._variantSelector
          .querySelectorAll('input[type="radio"]:checked')
          .forEach((inp) => {
            if (inp.dataset.optionPosition)
              values[inp.dataset.optionPosition] = inp.value;
          });
        this._variantSelector
          .querySelectorAll("select[data-option-position]")
          .forEach((sel) => {
            const opt = sel.options[sel.selectedIndex];
            if (opt) values[sel.dataset.optionPosition] = opt.value;
          });
        return (
          this._productData.variants.find((v) =>
            (v.options || []).every((val, i) => values[String(i + 1)] === val),
          ) || null
        );
      }

      /**
       * Syncs the subscription variant-plan `<select>` to the given variant,
       * preferring to keep the current plan type where an equivalent option exists.
       *
       * @param {number} variantId
       */
      _syncSubscriptionToVariant(variantId) {
        if (!this._variantPlanSelect) return;
        const varIdStr = String(variantId);
        const curPlanId =
          this._variantPlanSelect.options[this._variantPlanSelect.selectedIndex]
            ?.dataset.planId;
        const opts = Array.from(this._variantPlanSelect.options);

        const match =
          opts.find(
            (o) =>
              o.dataset.variantId === varIdStr &&
              o.dataset.planId === curPlanId,
          ) || opts.find((o) => o.dataset.variantId === varIdStr);
        if (!match) return;

        // Only the plan/variant select's own value tells us whether this call
        // actually changes anything. Without this guard, two widget instances
        // for the same product (e.g. main page + quick view) echo this sync's
        // republish back and forth forever: each receives the other's publish,
        // resolves the same already-current variant/plan, and republishes again.
        const changed = this._variantPlanSelect.value !== match.value;
        this._variantPlanSelect.value = match.value;
        this._updatePriceDisplay(
          parseInt(match.dataset.price),
          parseInt(match.dataset.compareAt),
        );

        // If subscription mode is currently active, persist the resolved plan and
        // variant to the form and URL so the add-to-cart always uses correct values.
        // Then re-publish optionValueSelectionChange so product-info aborts its
        // stale in-flight fetch (which started before selling_plan was in the URL)
        // and re-fetches with the correct selling_plan — rendering the right price.
        const isSubscriptionActive = !!this.querySelector(
          '[data-purchase-type="subscription"]:checked',
        );
        if (isSubscriptionActive) {
          this._setFormVariant(variantId);
          this._setSellingPlan(match.dataset.planId);
          this._updateUrlParam("variant", String(variantId));
          this._updateUrlParam("selling_plan", match.dataset.planId);
          // skipFocus: this sync follows an external picker change; don't pull
          // focus into the widget.
          if (changed) {
            this._publishVariantPlanOptionValues(this._variantPlanSelect, {
              skipFocus: true,
            });
          }
        }
      }

      /**
       * Syncs the one-time variant selector (radio buttons or selects) to reflect
       * the given variant. Mirror of `_syncSubscriptionToVariant`.
       *
       * @param {number} variantId
       */
      _syncOnetimeToVariant(variantId) {
        if (!this._variantSelector) return;
        const variant = this._productData.variants.find(
          (v) => v.id === variantId,
        );
        if (!variant) return;

        // Update radio buttons and their selected-value labels
        this._variantSelector
          .querySelectorAll('input[type="radio"][data-option-position]')
          .forEach((radio) => {
            const pos = parseInt(radio.dataset.optionPosition) - 1;
            if (variant.options[pos] !== radio.value) return;
            radio.checked = true;
            const label = radio
              .closest(".product-form__input")
              ?.querySelector("[data-selected-value]");
            if (label) label.textContent = radio.value;
          });

        // Update select elements
        this._variantSelector
          .querySelectorAll("select[data-option-position]")
          .forEach((sel) => {
            const pos = parseInt(sel.dataset.optionPosition) - 1;
            const targetValue = variant.options[pos];
            if (targetValue != null) sel.value = targetValue;
          });

        this._updateOnetimePrice(variant.price, variant.compare_at_price);
      }

      // ── Upsell: ATC button state ─────────────────────────────────────────────

      /**
       * Syncs the upsell ATC button's text and disabled state to the given variant.
       * Called whenever the selected variant changes inside the widget so the button
       * always reflects the correct availability without a page reload.
       *
       * @param {Object|null|undefined} variant — product JSON variant object
       */
      _updateAtcButtonState(variant) {
        const btn = this._atcSubmitBtn;
        if (!btn) return;
        const textEl = btn.querySelector(".btn__text");

        if (!variant) {
          btn.disabled = true;
          if (textEl) textEl.textContent = btn.dataset.labelUnavailable || "";
          return;
        }

        if (!variant.available) {
          btn.disabled = true;
          if (textEl) textEl.textContent = btn.dataset.labelSoldOut || "";
          return;
        }

        btn.disabled = false;
        if (textEl) textEl.textContent = btn.dataset.labelAvailable || "";
      }

      // ── Display helpers ───────────────────────────────────────────────────────

      /**
       * Updates the subscription price, strikethrough compare-at price, and
       * discount badge inside the widget.
       *
       * @param {number} price     — subscription price in cents
       * @param {number} compareAt — compare-at price in cents (0 if none)
       */
      _updatePriceDisplay(price, compareAt) {
        const priceEl = this.querySelector("[data-sub-price]");
        const compareEl = this.querySelector("[data-compare-price]");
        const badge = this.querySelector("[data-discount-badge]");

        if (priceEl) priceEl.textContent = this._formatMoney(price);

        if (compareEl) {
          compareEl.textContent =
            compareAt > price ? this._formatMoney(compareAt) : "";
          compareEl.style.display = compareAt > price ? "" : "none";
        }

        if (badge) {
          if (compareAt > price) {
            const pct = Math.round(((compareAt - price) * 100) / compareAt);
            badge.textContent = (
              badge.dataset.badgeTemplate || "SPARE __N__ %"
            ).replace("__N__", pct);
            badge.classList.remove("!hidden");
          } else {
            badge.classList.add("!hidden");
          }
        }
      }

      /**
       * Updates the one-time price display, including the strikethrough
       * compare-at price (shown only when it exceeds the current price).
       *
       * @param {number} price     — one-time price in cents
       * @param {number} compareAt — compare-at price in cents (0/null if none)
       */
      _updateOnetimePrice(price, compareAt = 0) {
        const show = compareAt > price;

        const el = this.querySelector("[data-onetime-price]");
        if (el) {
          el.textContent = this._formatMoney(price);
          // Sale colour only while a strikethrough compare-at price is shown.
          el.classList.toggle("bsw__onetime-price--sale", show);
        }

        const compareEl = this.querySelector("[data-onetime-compare-price]");
        if (compareEl) {
          compareEl.textContent = show ? this._formatMoney(compareAt) : "";
          compareEl.style.display = show ? "" : "none";
        }
      }

      /**
       * Updates the variant note from the re-rendered section HTML received via
       * the `variantChange` pubsub event. No extra fetch required — `product-info`
       * already fetched this document.
       *
       * @param {Document|null} html — parsed section HTML from `product-info`
       */
      _updateVariantNote(html) {
        console.log("update Variant Note", this)
        const el = this.querySelector("[data-variant-note]");
        console.log("Element to update", el)
        if (!el || !html) return;
        const source = html.querySelector("[data-variant-note]");
        console.log("source", source)
        if (!source) return;
        el.innerHTML = source.innerHTML;
        el.classList.toggle("!hidden", !source.innerHTML.trim());
      }

      // ── Form / URL helpers ────────────────────────────────────────────────────

      /**
       * Sets the hidden `id` input in the product form to the given variant ID.
       *
       * @param {number} variantId
       */
      _setFormVariant(variantId) {
        const input = this._form?.querySelector('[name="id"]');
        if (input) input.value = variantId;
      }

      /**
       * Creates or updates the hidden `selling_plan` input in the product form.
       *
       * @param {string|number} planId
       */
      _setSellingPlan(planId) {
        if (!this._form || !planId) return;
        let input = this._form.querySelector('[name="selling_plan"]');
        if (!input) {
          input = Object.assign(document.createElement("input"), {
            type: "hidden",
            name: "selling_plan",
          });
          this._form.appendChild(input);
        }
        input.value = planId;
      }

      /**
       * Removes the `selling_plan` hidden input from the product form.
       */
      _clearSellingPlan() {
        this._form?.querySelector('[name="selling_plan"]')?.remove();
      }

      /**
       * Updates a single URL search param without triggering navigation.
       *
       * @param {string} key
       * @param {string|number} value
       */
      _updateUrlParam(key, value) {
        if (this._isUpsell) return; // URL reflects the main product, not the upsell
        if(this._isQuickView){
          const url = this._productInfoElement?.dataset.url;
          if (url) {
            const parsedUrl = new URL(url, window.location.origin);
            parsedUrl.searchParams.set(key, String(value));
            this._productInfoElement.dataset.url = parsedUrl.toString().replace(window.location.origin, "");
          }
        }else{
          const url = new URL(window.location.href);
          url.searchParams.set(key, String(value));
          window.history.replaceState({}, "", url.toString());
        }

      }

      /**
       * Removes a single URL search param without triggering navigation.
       *
       * @param {string} key
       */
      _removeUrlParam(key) {
        if (this._isUpsell) return; // URL reflects the main product, not the upsell
        if(this._isQuickView){
          const url = this._productInfoElement?.dataset.url;
          if (url) {
            const parsedUrl = new URL(url, window.location.origin);
            parsedUrl.searchParams.delete(key);
            this._productInfoElement.dataset.url = parsedUrl.toString().replace(window.location.origin, "");
          }
        }else{
          const url = new URL(window.location.href);
          url.searchParams.delete(key);
          window.history.replaceState({}, "", url.toString());
        }
      }

      /**
       * Formats a price in cents to a localised money string using the active
       * storefront money format. Prefers the theme's `FoxTheme.Currency.formatMoney`
       * (which this storefront actually loads), then `Shopify.formatMoney`, then a
       * minimal numeric fallback.
       *
       * @param {number} cents
       * @returns {string}
       */
      _formatMoney(cents) {
        // Use the theme's own formatter — this storefront does not load
        // Shopify.formatMoney, so relying on it would always hit the fallback.
        if (window.FoxTheme?.Currency?.formatMoney)
          return FoxTheme.Currency.formatMoney(cents, this._moneyFormat);
        if (window.Shopify?.formatMoney)
          return Shopify.formatMoney(cents, this._moneyFormat);
        return (cents / 100).toFixed(2).replace(".", ",");
      }
    },
  );
}
